import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { popoverBox, usesSheet } from '../lib/callSettingsPlacement.js';
import { CogIcon } from './icons.js';

function coarsePointer(): boolean {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
}

/**
 * The gear and its "Call settings" panel. On narrow screens and touch devices the panel is a
 * full-width bottom sheet; on wide screens it is a popover clamped inside the viewport. The
 * children stay mounted while the panel is closed so that controls with their own timers and
 * cleanup (Professional) keep running.
 */
export function CallSettings({ children, badge = false }: { children: ReactNode; badge?: boolean }) {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(() => usesSheet(window.innerWidth, coarsePointer()));
  const [style, setStyle] = useState<CSSProperties>({});
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = useCallback(() => { setOpen(false); button.current?.focus(); }, []);

  const place = useCallback(() => {
    const isSheet = usesSheet(window.innerWidth, coarsePointer());
    setSheet(isSheet);
    if (isSheet || !button.current || !panel.current) { setStyle({}); return; }
    const a = button.current.getBoundingClientRect();
    const content = panel.current.scrollHeight;
    const box = popoverBox(a, content, { width: window.innerWidth, height: window.innerHeight });
    setStyle({ left: box.left, top: box.top, width: box.width, maxHeight: box.height });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', place);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('keydown', onKey); };
  }, [open, place, close]);

  return (
    <>
      <button ref={button} type="button" className="room-call-settings-button" aria-label="Call settings" title="Call settings"
        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        <CogIcon />{badge ? <span className="room-call-settings-badge" aria-hidden="true" /> : null}
      </button>
      {createPortal(
        <div className={`room-call-settings-layer${open ? ' open' : ''}`} hidden={!open}>
          <div className="room-call-settings-backdrop" onClick={close} />
          <div ref={panel} role="dialog" aria-modal={sheet} aria-label="Call settings" data-mode={sheet ? 'sheet' : 'popover'}
            className={`room-call-settings-panel ${sheet ? 'sheet' : 'popover'}`} style={style}>
            <header className="room-call-settings-head">
              <h2>Call settings</h2>
              <button type="button" className="room-call-settings-close" aria-label="Close call settings" onClick={close}>×</button>
            </header>
            <div className="room-call-settings-body">{children}</div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className="room-call-settings-section"><h3>{title}</h3>{children}</section>;
}
